import { IDrawBudgetItem, IDrawReceipt } from '@apply/api/src/api/DrawApi';
import React, { useEffect, useState } from 'react';
import { applicationInsights } from '../../../../../../../../apps/admin/src/ApplicationInsights';
import { DrawApi } from '@apply/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { DrawBudgetCategory } from '../../functions';
import { GenericPopup, Button, ButtonTheme, DropDownBox, MoneyBox, MultilineTextBox, FileSelectPopup } from '@apply/components';

interface IReceiptsModalV2Props {
    isOpen: boolean;
    categories: DrawBudgetCategory[];
    receipt?: IDrawReceipt;
    onSave: (receipt: IDrawReceipt) => void;
    onClose: () => void;
}
export interface IFileSelectDialogV2Props {
    ownerId?: string;
    isOpen: boolean;
    isReplace: boolean;
    fileTypeName?: string;
    maxFiles: number;
}
const ReceiptsModalV2: React.FC<IReceiptsModalV2Props> = ({ isOpen, categories, receipt, onSave, onClose }) => {
    const [selectedCategory, set_selectedCategory] = useState<DrawBudgetCategory>(categories[0]);
    const [fileSelectOptions, set_fileSelectOptions] = useState<IFileSelectDialogV2Props>({
        ownerId: undefined,
        isOpen: false,
        maxFiles: 1,
        isReplace: true,
        fileTypeName: `${categories[0]} Receipt`,
    });
    const [paymentAmount, set_paymentAmount] = useState<number>();
    const [note, set_note] = useState<string>();
    const [file, set_file] = useState<File | undefined>();

    const openFileDialog = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        const options = {
            ownerId: undefined,
            isOpen: true,
            maxFiles: 1,
            isReplace: true,
            fileTypeName: `${selectedCategory?.description} Receipt`,
        };
        set_fileSelectOptions(options);
    };

    const handleFileSubmit = (_: any, currentFiles: any) => {
        set_file(currentFiles[0]);

        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const handleReset = () => {
        onClose();
        set_paymentAmount(0);
        set_note('');
        set_selectedCategory(categories[0]);
        set_file(undefined);
    };

    const handleSave = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        onSave &&
            onSave({
                amount: paymentAmount,
                note: note,
                drawReceiptId: receipt?.drawReceiptId ?? 0,
                drawBudgetItemId: selectedCategory.id,
                file: file,
                downloadToken: undefined,
            });

        handleReset();
    };

    useEffect(() => {
        if (isOpen && receipt) {
            set_note(receipt.note);
            set_paymentAmount(receipt.amount);

            const category = categories.find((x) => x.id === receipt?.drawBudgetItemId);
            if (category) set_selectedCategory(category);
        }
    }, [isOpen]);

    const actionButtons = [
        {
            id: 'closeReceiptButton',
            theme: ButtonTheme.LinkBlue,
            disabled: false,
            children: 'Close',
            width: '100px',
            handleClick: handleReset,
        },
        {
            id: 'saveReceiptButton',
            theme: ButtonTheme.Primary,
            children: 'Save',
            width: '100px',
            disabled: !paymentAmount || (!file && !receipt?.fileName) || !selectedCategory,
            handleClick: handleSave,
        },
    ];

    return (
        <>
            {isOpen && (
                <>
                    <GenericPopup
                        id='drawReceiptsPopup'
                        isOpen={isOpen}
                        actionButtons={actionButtons}
                        handleClose={handleReset}
                        aria-labelledby='alert-dialog-title'
                        hideOnOutsideClick={true}
                        showCloseButton={true}
                        showTitle={true}
                        title='Add/Edit Receipts'
                        width='400px'
                        height='525px'
                        maxWidth='90%'
                        maxHeight='70%'
                        buttonAlignment='right'
                        wrapTitle={true}
                    >
                        <div>
                            <DropDownBox
                                id='receiptCategory'
                                value={selectedCategory?.id}
                                onChange={(e) => {
                                    const category = categories.find((x) => x.id === Number(e));
                                    if (category) set_selectedCategory(category);
                                }}
                                label='Category'
                                required={true}
                                dataSource={categories}
                                displayExpr='description'
                                valueExpr='id'
                            />
                            <MoneyBox
                                id='paymentAmount'
                                allowNegative={false}
                                value={paymentAmount}
                                onChange={(value) => {
                                    set_paymentAmount(value || 0);
                                }}
                                required={false}
                                label='Payment Amount'
                                displayChange={true}
                            />
                            <MultilineTextBox
                                id='notesId'
                                label='Notes For Receipt (Optional)'
                                placeholder='Notes...'
                                onChange={set_note}
                                value={note}
                            />
                            {!(file || receipt?.fileName) ? (
                                <Button
                                    id='uploadReceiptFileButton'
                                    dataTestid='uploadReceiptFileButton'
                                    handleClick={(e) => openFileDialog(e)}
                                    type='button'
                                    theme={ButtonTheme.Secondary}
                                    width='130px'
                                >
                                    Upload File
                                </Button>
                            ) : (
                                <div style={{ marginTop: 10, fontWeight: 600 }}>
                                    <FontAwesomeIcon
                                        icon={faFileInvoiceDollar}
                                        size={'1x'}
                                        className='color-green'
                                        style={{ marginLeft: 5, marginRight: 5 }}
                                    />
                                    {file?.name ?? receipt?.fileName}
                                </div>
                            )}
                        </div>
                    </GenericPopup>
                    <FileSelectPopup
                        id='addReceiptFileSelect'
                        options={fileSelectOptions}
                        onSubmit={handleFileSubmit}
                        onCancel={() =>
                            // @ts-ignore
                            set_fileSelectOptions((o) => ({
                                ...o,
                                isOpen: false,
                            }))
                        }
                    />
                </>
            )}
        </>
    );
};

export default ReceiptsModalV2;
